/**
 * SmoothClock — anchor + interpolate beat/phase between bridge ticks.
 *
 * The bridge emits ticks at 30Hz. Re-rendering the UI from raw ticks gives
 * ~33ms steppy motion, which is fine for a beat counter but bad for animated
 * visuals (LFO sine waves, beat rings, anything continuous).
 *
 * This module:
 *   - Records each incoming tick with the wall-clock time it arrived.
 *   - Exposes Solid signals (`beat`, `phase`, `bpm`) that update at 60Hz via
 *     requestAnimationFrame, interpolating from the last anchor.
 *
 * Authority: the bridge is still the source of truth. We never invent beats —
 * we extrapolate the bridge's last reading forward at the bridge's reported
 * tempo. When the next tick arrives, we re-anchor; tiny corrections are
 * absorbed naturally because each new anchor overrides the extrapolation.
 *
 * Phase wraparound is handled by recomputing phase from interpolated beat
 * modulo quantum (default 4), so phase stays in 0..1 across bar boundaries.
 *
 * Slew-correct (OPBridge clock-design ask, Build 2b, 2026-07-17): a naive
 * re-anchor hard-sets beat to the new anchor, so any gap between our
 * extrapolation and the fresh reading shows as a visible jump (typically a
 * sub-beat backward flick at 4 Hz polling). Instead we ease SMALL
 * corrections to zero over SLEW_MS: the displayed beat starts exactly where
 * it already was on-screen (no jump) and glides to truth. LARGE corrections
 * (a transport start, a seek, an iPad wake after background-throttling) are
 * NOT slewed — they snap, because gliding 100 beats over 180ms would look
 * like a fast scrub and the operator EXPECTS a jump when they moved the real
 * transport. The threshold is SLEW_MAX_BEATS.
 */

import { createSignal, onCleanup, type Accessor } from 'solid-js';

/** How long a small drift correction eases to zero, in ms. Must be shorter
 *  than the playing poll interval (250ms at 4 Hz) so each correction fully
 *  converges before the next anchor arrives. */
const SLEW_MS = 180;
/** Corrections larger than this (in beats) snap instead of easing — they're
 *  a real seek / start / resume, not drift. 4 Hz drift at 120 BPM is
 *  <0.02 beat, so 1 beat is comfortably above noise and below any musical
 *  jump the operator would make. */
const SLEW_MAX_BEATS = 1;

// Dev-time drift measurement (ask's "measurement discipline" clause). When
// on, each anchor logs the raw extrapolated-vs-anchor correction BEFORE
// slew hides it — a smooth display is not evidence the error is gone.
// Toggled from App via a `?clockdebug=1` URL param.
let debugDrift = false;
/** Enable/disable the per-anchor drift console log. */
export function setSmoothClockDebug(on: boolean): void { debugDrift = on; }

export interface SmoothClockHandle {
  /** Interpolated continuous beat. Updates ~60Hz. */
  beat: Accessor<number>;
  /** Interpolated phase 0..1 within current quantum-length bar. */
  phase: Accessor<number>;
  /** Last reported tempo. Updates on each tick. */
  bpm: Accessor<number>;
}

export interface SmoothClockOptions {
  /** Bar length in beats. Phase wraps on every `quantum` beats. Default 4. */
  quantum?: number;
}

export function createSmoothClock(opts: SmoothClockOptions = {}): SmoothClockHandle & {
  /** Call when a bridge tick arrives. Re-anchors interpolation. */
  anchor(tick: { beat: number; bpm: number }): void;
  /**
   * Freeze or thaw extrapolation. When frozen, beat/phase signals stay at
   * their last values; the RAF loop keeps running but stops advancing time.
   * Call `freeze(true)` when the bridge tells us Link is disabled — without
   * this the PWA would keep ticking from the last anchor even though the
   * bridge has gone silent.
   */
  freeze(frozen: boolean): void;
  /**
   * Change the bar length (beats per bar) that phase wraps on. Lets the
   * active clock source's time signature drive the phase strip / beat ring
   * (practice clock or live transport). Takes effect on the next frame;
   * ignored for non-positive values.
   */
  setQuantum(beats: number): void;
} {
  // Mutable so the active source's time-sig can retune bar length live.
  let quantum = opts.quantum ?? 4;

  let anchorBeat = 0;
  let anchorWallMs = performance.now();
  let anchorBpm = 120;
  let frozen = false;
  // Residual slew: how many beats the display is still offset from the
  // (post-anchor) truth, and when that offset started decaying. The frame
  // loop adds `slewOffset * remaining-fraction` on top of the raw
  // extrapolation, so the offset eases from its full value to 0 over
  // SLEW_MS. 0 = no correction in flight (steady state / snapped).
  let slewOffset = 0;
  let slewStartMs = 0;

  const [beat, setBeat] = createSignal(0);
  const [phase, setPhase] = createSignal(0);
  const [bpm, setBpm] = createSignal(120);

  /** Set beat + phase together from a single beat value. */
  function emit(b: number): void {
    setBeat(b);
    const mod = ((b % quantum) + quantum) % quantum;
    setPhase(mod / quantum);
  }

  let rafId: number | null = null;
  function frame() {
    if (!frozen) {
      const now = performance.now();
      const elapsedMin = (now - anchorWallMs) / 60_000;
      let b = anchorBeat + elapsedMin * anchorBpm;
      // Ease any in-flight correction to zero, then drop it once spent so
      // the display converges exactly onto the extrapolation (no permanent
      // lag behind truth).
      if (slewOffset !== 0) {
        const frac = Math.min(1, (now - slewStartMs) / SLEW_MS);
        if (frac >= 1) slewOffset = 0;
        else b += slewOffset * (1 - frac);
      }
      emit(b);
    }
    rafId = requestAnimationFrame(frame);
  }
  rafId = requestAnimationFrame(frame);

  onCleanup(() => {
    if (rafId !== null) cancelAnimationFrame(rafId);
  });

  return {
    beat,
    phase,
    bpm,
    anchor(tick) {
      // Correction = where the display currently sits minus where this
      // fresh reading says it should be. Computed against the on-screen
      // beat (which already carries any residual slew) so continuity holds
      // even mid-slew.
      const correction = beat() - tick.beat;
      anchorBeat = tick.beat;
      anchorWallMs = performance.now();
      anchorBpm = tick.bpm;
      setBpm(tick.bpm);
      if (debugDrift) {
        // eslint-disable-next-line no-console
        console.log(
          `[smoothClock] anchor beat=${tick.beat.toFixed(3)} correction=${correction.toFixed(3)}beat ` +
          `${Math.abs(correction) <= SLEW_MAX_BEATS && !frozen ? 'slew' : 'snap'}`,
        );
      }
      // Small drift eases; a large delta (seek/start/resume) or a frozen
      // clock snaps. Starting the display at (tick.beat + correction) ==
      // the current on-screen beat guarantees zero visible jump on a slew,
      // and the frame loop decays `correction` to 0 over SLEW_MS.
      if (!frozen && Math.abs(correction) <= SLEW_MAX_BEATS) {
        slewOffset = correction;
        slewStartMs = anchorWallMs;
        emit(tick.beat + correction);
      } else {
        slewOffset = 0;
        emit(tick.beat);
      }
    },
    freeze(next) {
      if (next === frozen) return;
      frozen = next;
      // A pending correction is meaningless across a freeze boundary (the
      // clock either stopped or is about to re-anchor on resume) — drop it.
      slewOffset = 0;
      // On thaw, re-anchor "now" to the current beat so we don't jump
      // forward by the entire frozen-duration on next frame.
      if (!frozen) {
        anchorBeat = beat();
        anchorWallMs = performance.now();
      }
    },
    setQuantum(beats) {
      if (beats > 0) quantum = beats;
    },
  };
}
